import { Link, useParams, Navigate } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Seo, SITE_URL } from "@/components/Seo";
import { getArticle, ARTICLES } from "@/data/articles";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";

const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

function Block({ block }) {
    if (block.type === "h2")
        return <h2 className="font-heading text-2xl sm:text-3xl text-ink mt-12 mb-4">{block.text}</h2>;
    if (block.type === "ul")
        return (
            <ul className="my-6 space-y-3">
                {block.items.map((it, i) => (
                    <li key={i} className="flex gap-3 text-inksoft leading-relaxed">
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        );
    if (block.type === "quote")
        return (
            <blockquote className="my-10 border-l-4 border-terracotta pl-6 py-1 font-heading text-2xl text-sage italic leading-snug">
                {block.text}
            </blockquote>
        );
    return <p className="text-inksoft leading-[1.8] text-[17px] my-5">{block.text}</p>;
}

export default function BlogArticle() {
    const { slug } = useParams();
    const article = getArticle(slug);

    if (!article) return <Navigate to="/blog" replace />;

    const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

    const articleJsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: article.title,
        description: article.description,
        image: article.heroImage,
        datePublished: article.date,
        dateModified: article.date,
        author: { "@type": "Organization", name: "DentAssists" },
        publisher: {
            "@type": "Organization",
            name: "DentAssists",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${article.slug}` },
        articleSection: article.category,
    };

    return (
        <div data-testid="blog-article-page" className="pt-[76px]">
            <Seo
                title={article.seoTitle}
                description={article.description}
                path={`/blog/${article.slug}`}
                image={article.heroImage}
                jsonLd={articleJsonLd}
            />

            <article className="max-w-[760px] mx-auto px-6 lg:px-8 pt-12 pb-8">
                <Reveal>
                    <Link
                        to="/blog"
                        data-testid="back-to-blog"
                        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-inksoft hover:text-terracotta transition-colors"
                    >
                        <ArrowLeft size={14} /> All resources
                    </Link>
                    <div className="flex items-center gap-3 mt-8 text-inksoft">
                        <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-terracotta">
                            {article.category}
                        </span>
                        <span className="text-linec">·</span>
                        <span className="font-mono text-[11px] uppercase tracking-[0.16em]">
                            {fmtDate(article.date)}
                        </span>
                        <span className="text-linec">·</span>
                        <span className="inline-flex items-center gap-1 text-xs">
                            <Clock size={12} /> {article.readTime}
                        </span>
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-ink mt-5 leading-[1.05]">
                        {article.title}
                    </h1>
                </Reveal>
            </article>

            <div className="max-w-[900px] mx-auto px-6 lg:px-8">
                <Reveal>
                    <div className="img-frame rounded-2xl border border-linec">
                        <img src={article.heroImage} alt={article.title} className="w-full h-[300px] lg:h-[440px] object-cover" />
                    </div>
                </Reveal>
            </div>

            <article className="max-w-[760px] mx-auto px-6 lg:px-8 py-10">
                {article.content.map((block, i) => (
                    <Block key={i} block={block} />
                ))}

                {/* CTA */}
                <div className="mt-14 bg-sage text-cream rounded-2xl p-8 lg:p-10">
                    <h3 className="font-heading text-2xl sm:text-3xl">Want this handled for you?</h3>
                    <p className="text-cream/75 mt-3 leading-relaxed">
                        DentAssists provides insurance verification, billing, AR management, and
                        credentialing for dental practices across Texas and the U.S. — inside your
                        existing software, HIPAA-compliant with a signed BAA.
                    </p>
                    <Link
                        to="/contact"
                        data-testid="article-cta"
                        className="inline-flex items-center gap-2 bg-terracotta text-cream px-7 py-3.5 rounded-sm font-medium mt-6 transition-[background-color,transform] duration-200 hover:bg-terracottadark hover:-translate-y-0.5"
                    >
                        Get a Free Consultation <ArrowRight size={17} />
                    </Link>
                </div>
            </article>

            {/* Related */}
            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pb-24">
                <h2 className="font-heading text-2xl text-ink mb-6">Keep reading</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    {related.map((a) => (
                        <Link
                            key={a.slug}
                            to={`/blog/${a.slug}`}
                            data-testid={`related-${a.slug}`}
                            className="group border border-linec rounded-2xl overflow-hidden hover:border-terracotta/50 transition-colors"
                        >
                            <div className="img-frame h-[150px]">
                                <img src={a.heroImage} alt={a.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-5">
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terracotta">
                                    {a.category}
                                </span>
                                <h3 className="font-heading text-lg text-ink mt-2 leading-snug group-hover:text-sage transition-colors">
                                    {a.title}
                                </h3>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
